import { roleLabel, type Account, type AccountRole } from './accountStore'

/**
 * Turning an audit event into words. An audit event records who did what to
 * which record, with the record before and after as JSON
 * (muster.audit_events, insert-only); this reads one back as a title and a
 * detail line, for the Overview's recent activity and for the audit log.
 * Names come from lookups of the accounts, employees, and teams the events
 * point at, so an event about a record that has since been deleted still
 * reads, with a generic noun in place of its name.
 */
export type ActivityKind = 'account' | 'role' | 'deactivated' | 'organization' | 'schedule'

export type AuditRow = {
  id: string
  actor: string | null
  action: string
  subject_table?: string
  subject_id: string
  previous_value: Record<string, unknown> | null
  new_value: Record<string, unknown> | null
  reason: string | null
  occurred_at: string
}

export type AuditLookups = {
  accounts: Map<string, Account>
  employees: Map<string, string>
  teams: Map<string, string>
}

export type DescribedEvent = { kind: ActivityKind; title: string; detail: string }

export type AuditCategory = { key: string; label: string; actions: string[] }

// The actions each kind of event records, so the log can be narrowed to one.
export const AUDIT_CATEGORIES: AuditCategory[] = [
  {
    key: 'accounts',
    label: 'Accounts',
    actions: ['account_created', 'account_updated', 'account_role_changed', 'account_deactivated', 'account_reactivated'],
  },
  {
    key: 'teams',
    label: 'Teams',
    actions: ['team_created', 'team_updated', 'team_closed', 'team_reopened', 'team_supervisor_assigned'],
  },
  { key: 'personnel', label: 'Transfers and details', actions: ['employee_transferred', 'detail_created'] },
  {
    key: 'catalog',
    label: 'Catalogs',
    actions: [
      'shift_template_created',
      'shift_template_updated',
      'shift_template_deactivated',
      'shift_template_reactivated',
      'status_code_updated',
      'assignment_created',
      'assignment_updated',
      'assignment_deactivated',
      'assignment_reactivated',
      'activity_created',
      'activity_updated',
      'activity_deactivated',
      'activity_reactivated',
      'leave_type_created',
      'leave_type_updated',
      'leave_type_deactivated',
      'leave_type_reactivated',
    ],
  },
  {
    key: 'staffing',
    label: 'Staffing targets and defaults',
    actions: ['staffing_target_created', 'staffing_target_removed', 'schedule_default_set', 'schedule_default_cleared'],
  },
  {
    key: 'leave',
    label: 'Leave requests',
    actions: [
      'leave_request_submitted',
      'leave_request_resubmitted',
      'leave_request_returned',
      'leave_request_approved',
      'leave_request_denied',
      'leave_request_decision_reversed',
      'leave_request_cancelled',
    ],
  },
  {
    key: 'schedules',
    label: 'Schedules',
    actions: [
      'schedule_submission_submitted',
      'schedule_submission_resubmitted',
      'schedule_submission_returned',
      'schedule_submission_approved',
      'schedule_submission_denied',
      'schedule_submission_decision_reversed',
      'schedule_published',
      'schedule_amended',
    ],
  },
]

// What an administrator manages: accounts, teams, and the personnel and
// configuration changes made across the pilot. Routine workflow (a leave
// request, a schedule submission) is not administration.
export const ADMINISTRATIVE_ACTIONS = AUDIT_CATEGORIES.filter((category) =>
  ['accounts', 'teams', 'personnel', 'catalog', 'staffing'].includes(category.key),
).flatMap((category) => category.actions)

function isRole(value: unknown): value is AccountRole {
  return value === 'agent' || value === 'supervisor' || value === 'leadership' || value === 'administrator'
}

export function accountName(account: Account | undefined): string {
  return account ? `${account.firstName} ${account.lastName}` : 'An account'
}

function text(value: Record<string, unknown> | null, key: string): string | null {
  return typeof value?.[key] === 'string' ? (value[key] as string) : null
}

// "leave_request_submitted" as "Leave request submitted", for an action
// nothing above names.
function readable(action: string): string {
  const words = action.replace(/_/g, ' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export function describeAuditEvent(row: AuditRow, lookups: AuditLookups): DescribedEvent {
  const actor = row.actor ? accountName(lookups.accounts.get(row.actor)) : 'The system'
  const by = `by ${actor}`
  const item = (kind: ActivityKind, title: string, detail: string): DescribedEvent => ({ kind, title, detail })

  const subjectAccount = accountName(lookups.accounts.get(row.subject_id))
  const subjectEmployee = lookups.employees.get(row.subject_id) ?? 'An employee'
  const subjectTeam = lookups.teams.get(row.subject_id) ?? 'A team'
  const teamOf = (value: Record<string, unknown> | null) => lookups.teams.get(text(value, 'team_id') ?? '') ?? 'a team'
  // A leave request or a schedule submission names its employee in the record
  // itself; the event's own subject is the request.
  const employeeOf = (value: Record<string, unknown> | null) =>
    lookups.employees.get(text(value, 'employee_id') ?? '') ?? 'An employee'
  const dates = (value: Record<string, unknown> | null, from: string, to: string) => {
    const start = text(value, from)
    const end = text(value, to)
    return start && end ? (start === end ? start : `${start} to ${end}`) : null
  }
  const withDates = (range: string | null) => (range ? `${range}, ` : '')

  switch (row.action) {
    case 'team_created':
      return item('organization', `Team created · ${text(row.new_value, 'name') ?? subjectTeam}`, by)
    case 'team_updated': {
      const before = text(row.previous_value, 'name')
      const after = text(row.new_value, 'name') ?? subjectTeam
      return item('organization', `Team updated · ${after}`, before && before !== after ? `Renamed from ${before}, ${by}` : by)
    }
    case 'team_closed': {
      const closes = text(row.new_value, 'closeDate')
      return item('organization', `Team closed · ${text(row.new_value, 'name') ?? subjectTeam}`, `${closes ? `Closes ${closes}, ` : ''}${by}`)
    }
    case 'team_reopened':
      return item('organization', `Team reopened · ${text(row.new_value, 'name') ?? subjectTeam}`, by)
    case 'team_supervisor_assigned': {
      const nowId = text(row.new_value, 'supervisorId')
      const beforeId = text(row.previous_value, 'supervisorId')
      const before = accountName(lookups.accounts.get(beforeId ?? ''))
      if (!nowId) return item('organization', `Supervisor removed · ${subjectTeam}`, `${before}, ${by}`)
      const now = accountName(lookups.accounts.get(nowId))
      return item('organization', `Supervisor assigned · ${subjectTeam}`, `${now}${beforeId ? `, replacing ${before}` : ''}, ${by}`)
    }
    case 'account_created': {
      const role = row.new_value?.role
      return item('account', `Account created · ${subjectAccount}`, `${isRole(role) ? roleLabel(role) : 'Account'}, ${by}`)
    }
    case 'account_updated':
      return item('account', `Account updated · ${subjectAccount}`, by)
    case 'account_role_changed': {
      const before = row.previous_value?.role
      const after = row.new_value?.role
      const change = isRole(before) && isRole(after) ? `${roleLabel(before)} → ${roleLabel(after)}, ` : ''
      return item('role', `Role changed · ${subjectAccount}`, `${change}${by}`)
    }
    case 'account_deactivated':
      return item('deactivated', `Account deactivated · ${subjectAccount}`, row.reason ? `${row.reason}, ${by}` : by)
    case 'account_reactivated':
      return item('account', `Account reactivated · ${subjectAccount}`, by)
    case 'employee_transferred':
      return item('organization', `Transferred · ${subjectEmployee}`, `To ${teamOf(row.new_value)}, ${by}`)
    case 'detail_created': {
      const destination = row.new_value?.destination
      return item('organization', `Detail created · ${employeeOf(row.new_value)}`, `${typeof destination === 'string' ? `${destination}, ` : ''}${by}`)
    }
    case 'staffing_target_created':
      return item('schedule', 'Staffing target added', `${teamOf(row.new_value)}, ${by}`)
    case 'staffing_target_removed':
      return item('schedule', 'Staffing target removed', `${teamOf(row.previous_value)}, ${by}`)
    case 'schedule_default_set':
      return item('schedule', `Schedule default set · ${employeeOf(row.new_value)}`, by)
    case 'schedule_default_cleared':
      return item('schedule', `Schedule default cleared · ${employeeOf(row.previous_value)}`, by)
    case 'shift_template_created': {
      const start = text(row.new_value, 'start_time')?.slice(0, 5)
      const end = text(row.new_value, 'end_time')?.slice(0, 5)
      return item('schedule', `Shift template created · ${text(row.new_value, 'name') ?? 'A shift template'}`, `${start && end ? `${start} to ${end}, ` : ''}${by}`)
    }
    case 'shift_template_updated': {
      const before = text(row.previous_value, 'name')
      const after = text(row.new_value, 'name') ?? 'A shift template'
      return item('schedule', `Shift template updated · ${after}`, before && before !== after ? `Renamed from ${before}, ${by}` : by)
    }
    case 'shift_template_deactivated':
      return item('schedule', `Shift template retired · ${text(row.new_value, 'name') ?? 'A shift template'}`, by)
    case 'shift_template_reactivated':
      return item('schedule', `Shift template restored · ${text(row.new_value, 'name') ?? 'A shift template'}`, by)
    case 'status_code_updated': {
      const changes: string[] = []
      const before = text(row.previous_value, 'category')
      const after = text(row.new_value, 'category')
      if (before && after && before !== after) changes.push(`${readable(before)} to ${readable(after)}`)
      const wasAvailable = row.previous_value?.counts_as_available
      const isAvailable = row.new_value?.counts_as_available
      if (typeof wasAvailable === 'boolean' && typeof isAvailable === 'boolean' && wasAvailable !== isAvailable) {
        changes.push(isAvailable ? 'now counts as available' : 'no longer counts as available')
      }
      return item('schedule', `Status code updated · ${text(row.new_value, 'name') ?? 'A status code'}`, `${changes.length ? `${changes.join(', ')}, ` : ''}${by}`)
    }
    case 'assignment_created':
      return item('schedule', `Assignment created · ${text(row.new_value, 'name') ?? 'An assignment'}`, `${text(row.new_value, 'duty_category') ? `${text(row.new_value, 'duty_category')}, ` : ''}${by}`)
    case 'assignment_updated': {
      const changes: string[] = []
      const was = text(row.previous_value, 'duty_category')
      const now = text(row.new_value, 'duty_category')
      if (was && now && was !== now) changes.push(`Duty category ${was} to ${now}`)
      if (row.previous_value?.team_id !== row.new_value?.team_id) changes.push('team changed')
      if (row.previous_value?.sort_priority !== row.new_value?.sort_priority) changes.push('priority changed')
      if (row.previous_value?.required_qualification_id !== row.new_value?.required_qualification_id) changes.push('qualification changed')
      if (row.previous_value?.default_activity_id !== row.new_value?.default_activity_id) changes.push('default activity changed')
      return item('schedule', `Assignment updated · ${text(row.new_value, 'name') ?? 'An assignment'}`, `${changes.length ? `${changes.join(', ')}, ` : ''}${by}`)
    }
    case 'assignment_deactivated':
      return item('schedule', `Assignment retired · ${text(row.new_value, 'name') ?? 'An assignment'}`, by)
    case 'assignment_reactivated':
      return item('schedule', `Assignment restored · ${text(row.new_value, 'name') ?? 'An assignment'}`, by)
    case 'activity_created':
      return item('schedule', `Activity created · ${text(row.new_value, 'name') ?? 'An activity'}`, `${row.new_value?.parent_activity_id ? 'Sub-activity, ' : ''}${by}`)
    case 'activity_updated': {
      const before = text(row.previous_value, 'name')
      const after = text(row.new_value, 'name') ?? 'An activity'
      return item('schedule', `Activity updated · ${after}`, before && before !== after ? `Renamed from ${before}, ${by}` : by)
    }
    case 'activity_deactivated':
      return item('schedule', `Activity retired · ${text(row.new_value, 'name') ?? 'An activity'}`, by)
    case 'activity_reactivated':
      return item('schedule', `Activity restored · ${text(row.new_value, 'name') ?? 'An activity'}`, by)
    case 'leave_type_created':
      return item('schedule', `Leave type created · ${text(row.new_value, 'name') ?? 'A leave type'}`, by)
    case 'leave_type_updated': {
      const before = text(row.previous_value, 'name')
      const after = text(row.new_value, 'name') ?? 'A leave type'
      return item('schedule', `Leave type updated · ${after}`, before && before !== after ? `Renamed from ${before}, ${by}` : by)
    }
    case 'leave_type_deactivated':
      return item('schedule', `Leave type retired · ${text(row.new_value, 'name') ?? 'A leave type'}`, by)
    case 'leave_type_reactivated':
      return item('schedule', `Leave type restored · ${text(row.new_value, 'name') ?? 'A leave type'}`, by)
    case 'leave_request_submitted':
    case 'leave_request_resubmitted':
    case 'leave_request_returned':
    case 'leave_request_approved':
    case 'leave_request_denied':
    case 'leave_request_cancelled': {
      const state = row.action.replace('leave_request_', '')
      return item(
        'schedule',
        `Leave request ${state} · ${employeeOf(row.new_value ?? row.previous_value)}`,
        `${withDates(dates(row.new_value ?? row.previous_value, 'start_date', 'end_date'))}${by}`,
      )
    }
    case 'leave_request_decision_reversed':
      return item(
        'schedule',
        `Leave decision reversed · ${employeeOf(row.new_value ?? row.previous_value)}`,
        `${withDates(dates(row.new_value ?? row.previous_value, 'start_date', 'end_date'))}${by}`,
      )
    case 'schedule_submission_submitted':
    case 'schedule_submission_resubmitted':
    case 'schedule_submission_returned':
    case 'schedule_submission_approved':
    case 'schedule_submission_denied': {
      const state = row.action.replace('schedule_submission_', '')
      return item(
        'schedule',
        `Schedule ${state} · ${employeeOf(row.new_value ?? row.previous_value)}`,
        `${withDates(dates(row.new_value ?? row.previous_value, 'period_start', 'period_end'))}${by}`,
      )
    }
    case 'schedule_submission_decision_reversed':
      return item(
        'schedule',
        `Schedule approval reversed · ${employeeOf(row.new_value ?? row.previous_value)}`,
        `${withDates(dates(row.new_value ?? row.previous_value, 'period_start', 'period_end'))}${by}`,
      )
    case 'schedule_published':
    case 'schedule_amended': {
      const period = text(row.new_value, 'period_start')
      return item(
        'schedule',
        `Schedule ${row.action === 'schedule_published' ? 'published' : 'amended'} · ${teamOf(row.new_value)}`,
        `${period ? `Pay period from ${period}, ` : ''}${by}`,
      )
    }
    default:
      return item('schedule', readable(row.action), by)
  }
}
